// The Browse All style panel: the bottom-of-the-home-page call to action, in ONE surface with a
// live example pinned above it.
//
// It replaces the Home editor's inline Content / Background / Heading / Button Pivot panel. Same
// reasoning as the product card and the category pod (see ProductCardStylePanel /
// CategoryPodStylePanel): the parts are one visual object, tabs hid three quarters of it behind a
// click, and the subheading's font settings were tucked into the bottom of the Heading tab where
// nobody found them.
//
// It also covers BOTH styles the storefront can render here, not just the band: with no config the
// shopper gets the classic centered button, so the enable toggle (and the example above it) is the
// first thing in the panel rather than a checkbox buried in a tab.
//
// The groups are the Storefront Styles panel's part accordions, one open at a time, each ending in
// the same "Advanced…" escape into the raw key/value CSS editor.
//
// The panel is deliberately dumb: it owns no state beyond which group is open. The Home editor
// passes the band object out of its own config blob and takes the whole replacement back.

import React from 'react';
import { IconButton, Panel, PanelType, TextField, Toggle } from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import { IBrowseAllBand } from './homePageConfig';
import { ISearchElementStyle, ISearchTextStyle } from './searchPageConfig';
import {
    advancedReplacementPatch,
    AdvancedStyleButton,
    elementStyleFrom,
    ElementPropertyRows,
    parseNum,
    StyleGroupAccordion,
    StyleOverridesGroup,
    textStyleFrom,
} from './styleControls';
import { STYLE_PART_ICONS, styleTargetForKey } from './stylesConfig';
import { ColorRow, StyleRow, useCellControlStyles } from './styleRows';
import StyleExampleFrame from './StyleExampleFrame';
import StorefrontSectionAdvancedStyleEditorModal from '../StorefrontSectionAdvancedStyleEditorModal';
import StorefrontBannerImageUploader from './Banners/StorefrontBannerImageUploader';
import Setting from '../../Common/Setting';
import ConditionalSetting from '../../Common/ConditionalSetting';
import './StorefrontEditor.css';

// The fields that only exist once the band is on - the registry entries this panel's
// `{value && <>...</>}` block guards. Kept as one list so the ConditionalSetting wrapper and the
// registry stay obviously in step.
const BROWSE_ALL_BAND_FIELD_IDS = [
    'storefront.browseAll.heading',
    'storefront.browseAll.subheading',
    'storefront.browseAll.overlayOpacity',
    'storefront.browseAll.minHeight',
    'storefront.browseAll.fixed',
] as const;

// ISearchTextStyle is narrower than the ISearchElementStyle the generic rows speak, so the dicts
// don't structurally interchange in either direction, even though at RUNTIME they are all open
// camelCase CSS bags with an index signature. One loose cast at the boundary, in both directions,
// exactly as CategoryPodStylePanel does it.
const loose = (dict?: { [prop: string]: any }): any => dict;

// Hex to rgba for the overlay wash. Copied from the live HomePage.tsx so the tint composes
// identically here: a value that isn't a 3/6-digit hex (rgb(), rgba(), a named color) is handed
// back untouched rather than mangled.
const toRgba = (c: string, a: number): string => {
    const h = c.replace('#', ''); const n = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    if (n.length < 6) return c; // already rgb()/rgba()/named, use as-is
    return `rgba(${parseInt(n.slice(0, 2), 16)}, ${parseInt(n.slice(2, 4), 16)}, ${parseInt(n.slice(4, 6), 16)}, ${a})`;
};

/**
 * The band's own background box, composed from the discrete config fields exactly as the live
 * storefront composes it (HomePage.tsx): a solid color, an image, and an optional tint wash over
 * the image, with an optional fixed attachment.
 *
 * Deliberately WITHOUT any full-bleed trick: the canvas preview spreads fullBleedFromColor over
 * this to paint the color edge to edge, and the panel example wants the plain box.
 */
export const browseAllBandStyle = (band: IBrowseAllBand): React.CSSProperties => {
    const layers: string[] = [];
    if (band.backgroundImageUrl && band.overlayColor) {
        const wash = toRgba(band.overlayColor, (band.overlayOpacity ?? 50) / 100);
        layers.push(`linear-gradient(${wash}, ${wash})`);
    }
    if (band.backgroundImageUrl) layers.push(`url("${band.backgroundImageUrl}")`);
    return {
        minHeight: band.minHeight ?? 320,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: band.backgroundColor,
        backgroundImage: layers.length ? layers.join(', ') : undefined,
        backgroundSize: band.backgroundImageUrl ? 'cover' : undefined,
        backgroundPosition: band.backgroundImageUrl ? 'center' : undefined,
        backgroundRepeat: band.backgroundImageUrl ? 'no-repeat' : undefined,
        backgroundAttachment: band.fixed ? 'fixed' : undefined,
    };
};

/**
 * What sits inside the band: the centered heading / subheading / button column. Shared by the
 * canvas preview and this panel's example so the composition is authored exactly once.
 *
 * `condensed` tightens the vertical padding for the 340px panel, where the full 56px would push
 * the button out of the example frame.
 */
/**
 * The Browse All button's classes. The dark variant carries the light hairline border the live
 * store gets from its theme stylesheet, which the editor canvas does not load — see
 * `.browse-all-button-dark` in StorefrontEditor.css.
 */
export const browseAllButtonClass = (isDark?: boolean): string =>
    `browse-all-button${isDark ? ' browse-all-button-dark' : ''}`;

export const BrowseAllBandContent: React.FC<{
    band: IBrowseAllBand;
    buttonText: string;
    condensed?: boolean;
    /** Dark storefront background — switches the button to the light-hairline variant. */
    isDark?: boolean;
}> = ({ band, buttonText, condensed, isDark }) => {
    // textStyleFrom emits `fontSize: undefined` when unset, which would clobber the default via
    // the spread, so the size is applied AFTER the spread with a fallback (mirrors HomePage.tsx).
    const hs = textStyleFrom(band.headingStyle, 'heading');
    const ss = textStyleFrom(band.subheadingStyle, 'body');
    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', gap: '0.9em', padding: condensed ? '24px 12px' : '56px 24px',
            maxWidth: 860, margin: '0 auto',
        }}>
            {band.heading && <div style={{ fontWeight: 700, lineHeight: 1.12, ...hs, fontSize: hs.fontSize ?? 'clamp(2rem, 4.5vw, 3rem)' }}>{band.heading}</div>}
            {band.subheading && <div style={{ lineHeight: 1.55, ...ss, fontSize: ss.fontSize ?? 'clamp(1rem, 2vw, 1.2rem)' }}>{band.subheading}</div>}
            <button type='button' className={browseAllButtonClass(isDark)} style={elementStyleFrom(band.buttonStyle)}>{buttonText}</button>
        </div>
    );
};

// The Browse All area exactly as the canvas and the live store draw it (same class, same band
// composition, same text styles), condensed to fit the panel: the band's padding tightens and its
// height is capped, so a 320px+ band doesn't push the button below the fold of a 340px panel.
const EXAMPLE_MAX_HEIGHT = 160;

const BrowseAllExample: React.FC<{
    band?: IBrowseAllBand;
    buttonText: string;
    isDark?: boolean;
}> = ({ band, buttonText, isDark }) => (
    <StyleExampleFrame
        isDark={isDark}
        caption={band ? 'Example (condensed), updates as you edit' : 'Example, updates as you edit'}
    >
        {band
            ? (
                <div style={{ width: '100%', ...browseAllBandStyle(band), minHeight: Math.min(band.minHeight ?? 320, EXAMPLE_MAX_HEIGHT) }}>
                    <BrowseAllBandContent band={band} buttonText={buttonText} condensed isDark={isDark} />
                </div>
            )
            // No band: the classic button, unstyled, the way the live store renders it when the
            // config is absent (it only applies buttonStyle inside a band).
            : <button type='button' className={browseAllButtonClass(isDark)}>{buttonText}</button>}
    </StyleExampleFrame>
);

export interface IBrowseAllStylePanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** Undefined = the classic centered button (no band configured). */
    value?: IBrowseAllBand;
    /** Replaces the WHOLE band object; undefined turns the band off. */
    onChange: (band?: IBrowseAllBand) => void;
    /** The localized Browse All label, resolved by the editor from the storefront's localization. */
    buttonText: string;
    isDark?: boolean;
}

const BrowseAllStylePanel: React.FC<IBrowseAllStylePanelProps> = (props) => {
    const { value } = props;
    const cell = useCellControlStyles();

    // The band is handed back whole (the enable toggle has to be able to remove it), so the merge
    // helpers live here rather than in the editor: one for the band's own fields, one per nested
    // style dict.
    const update = (patch: Partial<IBrowseAllBand>) => props.onChange({ ...value, ...patch });
    const updateHeadingStyle = (patch: Partial<ISearchTextStyle>) => update({ headingStyle: { ...value?.headingStyle, ...patch } });
    const updateSubheadingStyle = (patch: Partial<ISearchTextStyle>) => update({ subheadingStyle: { ...value?.subheadingStyle, ...patch } });
    const updateButtonStyle = (patch: Partial<ISearchElementStyle>) => update({ buttonStyle: { ...value?.buttonStyle, ...patch } });

    // One group open at a time, seeded on the words (the part a merchant reaches for first): the
    // same accordion behaviour as the Styles panel's part list, so the surface is a short scannable
    // list rather than five full property sets stacked end to end.
    const [openGroup, setOpenGroup] = React.useState('text');
    const group = (key: string) => ({
        expanded: openGroup === key,
        onToggle: (expanded: boolean) => setOpenGroup(expanded ? key : ''),
    });

    // The raw key/value CSS editor, one entry per style dict. It hands back the WHOLE dict rather
    // than a patch (a key deleted there has to disappear), so each entry replaces its dict outright
    // instead of merging. The updaters above stay the merge path for the flat rows.
    const [advancedGroup, setAdvancedGroup] = React.useState<string | undefined>();
    const advanced: Record<string, { value?: Record<string, any>; replace: (css: Css) => void }> = {
        heading: { value: value?.headingStyle, replace: (css) => updateHeadingStyle(advancedReplacementPatch(value?.headingStyle, css)) },
        subheading: { value: value?.subheadingStyle, replace: (css) => updateSubheadingStyle(advancedReplacementPatch(value?.subheadingStyle, css)) },
        button: { value: value?.buttonStyle, replace: (css) => updateButtonStyle(advancedReplacementPatch(value?.buttonStyle, css)) },
    };

    // Read-only stand-in for the band while ConditionalSetting is ghosting this block (`value` is
    // genuinely undefined then - that's why it's ghosted). Every WRITE below still closes over the
    // real `value`/`update*` from props/state, which is correct either way and never fires in ghost
    // mode (interaction is blocked at the wrapper).
    const bandView: IBrowseAllBand = value ?? {};

    return (
        <>
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Browse All'
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* Pinned above the groups so the effect of every row below is on screen without
                scrolling back to the preview canvas, and stays pinned while the groups scroll on
                viewports tall enough to afford it (sf-card-example-sticky, StorefrontEditor.css). */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <BrowseAllExample band={value} buttonText={props.buttonText} isDark={props.isDark} />
            </div>

            {/* Enabling seeds a heading and a height so the band is visible immediately (an empty
                band is a blank strip); an existing band's own values win over the seed. Disabling
                drops the object entirely, back to the classic button. */}
            <Setting id='storefront.browseAll.enabled'>
                <Toggle
                    label='Browse All band'
                    inlineLabel
                    checked={!!value}
                    onChange={(_, checked) => props.onChange(checked ? { heading: 'Get Started', minHeight: 320, ...value } : undefined)}
                />
            </Setting>
            <div style={{ fontSize: 11, color: '#605e5c' }}>
                Turns the bottom &ldquo;Browse All&rdquo; area into a full-width call-to-action band with the button inside it.
                Off, the shopper gets the classic centered button.
            </div>

            {/* No StyleRowsWidthProvider: this is a 340px smallFixedFar panel, the width the flat
                rows' default 180px control column was tuned for. */}
            <ConditionalSetting
                when={!!value}
                ids={BROWSE_ALL_BAND_FIELD_IDS}
                idPrefix={['storefront.browseAll.heading', 'storefront.browseAll.subheading', 'storefront.browseAll.button']}
            >
                <StyleGroupAccordion
                    label='Text'
                    iconName={STYLE_PART_ICONS.title}
                    tooltip='The words on the band. Both lines are optional; with neither set the band is just the button on its background.'
                    {...group('text')}
                >
                    <Setting id='storefront.browseAll.heading'>
                        <TextField label='Heading' value={bandView.heading ?? ''} onChange={(_, v) => update({ heading: v || undefined })} />
                    </Setting>
                    <Setting id='storefront.browseAll.subheading'>
                        <TextField label='Subheading' multiline autoAdjustHeight value={bandView.subheading ?? ''} onChange={(_, v) => update({ subheading: v || undefined })} />
                    </Setting>
                </StyleGroupAccordion>

                <StyleGroupAccordion
                    label='Background'
                    iconName={STYLE_PART_ICONS.band}
                    tooltip='The band behind the call to action: a solid color, an image, or an image with a tinted wash over it.'
                    {...group('background')}
                >
                    <ColorRow label='Band color' value={bandView.backgroundColor} onChange={(v) => update({ backgroundColor: v })} />
                    {/* Background image: the shared asset chooser / uploader tile (pick from the
                        asset library, or plain upload where the library context is absent), not a
                        raw URL field. The rows' 180x32 control cell is too small to preview an
                        image, so the tile sits under its own label line, the way the affiliations
                        and nav pod editors present theirs. A hand-written URL remains possible
                        through the Home Page Style raw JSON (backgroundImageUrl). */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 32 }}>
                            <span style={{ fontSize: 13 }}>Image</span>
                            {bandView.backgroundImageUrl && (
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title='Remove the background image'
                                    ariaLabel='Remove the background image'
                                    onClick={() => update({ backgroundImageUrl: undefined })}
                                />
                            )}
                        </div>
                        <div style={{ width: '100%', height: 100, border: '1px dashed #c8c6c4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <StorefrontBannerImageUploader
                                value={bandView.backgroundImageUrl}
                                onChange={(url) => update({ backgroundImageUrl: url })}
                                width={260}
                                height={100}
                                isNew={!bandView.backgroundImageUrl}
                            />
                        </div>
                    </div>
                    <ColorRow label='Overlay tint' value={bandView.overlayColor} onChange={(v) => update({ overlayColor: v })} />
                    <Setting id='storefront.browseAll.overlayOpacity'>
                        <StyleRow label='Overlay'>
                            <TextField type='number' min={0} max={100} suffix='%' value={bandView.overlayOpacity?.toString() ?? ''} onChange={(_, v) => update({ overlayOpacity: parseNum(v) })} styles={cell.textField} />
                        </StyleRow>
                    </Setting>
                    <Setting id='storefront.browseAll.minHeight'>
                        <StyleRow label='Min height'>
                            <TextField type='number' min={0} suffix='px' value={bandView.minHeight?.toString() ?? ''} onChange={(_, v) => update({ minHeight: parseNum(v) })} styles={cell.textField} />
                        </StyleRow>
                    </Setting>
                    <Setting id='storefront.browseAll.fixed'>
                        <Toggle label='Fixed background (parallax)' inlineLabel checked={!!bandView.fixed} onChange={(_, c) => update({ fixed: c || undefined })} />
                    </Setting>
                </StyleGroupAccordion>

                <StyleGroupAccordion
                    label='Heading'
                    iconName={STYLE_PART_ICONS.heading}
                    tooltip='The large headline. Font settings here style that line only.'
                    {...group('heading')}
                >
                    <ElementPropertyRows value={loose(bandView.headingStyle)} settingKeyPrefix='storefront.browseAll.heading' onChange={(patch) => updateHeadingStyle(loose(patch))} />
                    <AdvancedStyleButton onClick={() => setAdvancedGroup('heading')} />
                </StyleGroupAccordion>

                <StyleGroupAccordion
                    label='Subheading'
                    iconName={STYLE_PART_ICONS.body}
                    tooltip='The smaller line under the headline. Font settings here style that line only.'
                    {...group('subheading')}
                >
                    <ElementPropertyRows value={loose(bandView.subheadingStyle)} settingKeyPrefix='storefront.browseAll.subheading' onChange={(patch) => updateSubheadingStyle(loose(patch))} />
                    <AdvancedStyleButton onClick={() => setAdvancedGroup('subheading')} />
                </StyleGroupAccordion>

                {/* The only part here that maps to a style element type (button/base), so it's the
                    only group with a named-style picker. The Advanced… escape sits AFTER the
                    overrides group: it edits the same dict, but it isn't one of the overrides the
                    fold counts, and inside the fold it would disappear whenever it's collapsed. */}
                <StyleGroupAccordion
                    label='Button'
                    iconName={STYLE_PART_ICONS.base}
                    tooltip="The Browse All button itself. The storefront's default is the plain outlined button."
                    {...group('button')}
                >
                    <StyleOverridesGroup
                        pickerLabel='Style'
                        rowLayout
                        target={styleTargetForKey('buttonStyle')}
                        value={bandView.buttonStyle}
                        onChange={updateButtonStyle}
                    >
                        <ElementPropertyRows value={bandView.buttonStyle} settingKeyPrefix='storefront.browseAll.button' onChange={updateButtonStyle} />
                    </StyleOverridesGroup>
                    <AdvancedStyleButton onClick={() => setAdvancedGroup('button')} />
                </StyleGroupAccordion>
            </ConditionalSetting>
        </Panel>

        {/* Rendered once, outside the accordions: the raw CSS editor for whichever group asked. */}
        {advancedGroup && advanced[advancedGroup] && (
            <StorefrontSectionAdvancedStyleEditorModal
                css={(advanced[advancedGroup].value ?? {}) as Css}
                displayModal
                closer={() => setAdvancedGroup(undefined)}
                onChange={(css) => advanced[advancedGroup].replace(css)}
            />
        )}
        </>
    );
};

export default BrowseAllStylePanel;
